'use client';

import { useEffect, useState } from 'react';
import { buttonVariants } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { getQueryParam } from '@/lib/query-params';
import Link from 'next/link';
import { Check, ArrowRight, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PRICING_TIERS, BillingType } from '@/types';
import { useIntersectionObserver } from '@/app/hooks/useIntersectionObserver';
import {
  trackPricingSelection,
  getAbVariant,
  trackAbExposure,
  trackViewItemList,
  trackSelectItem,
  AbVariant,
} from '@/components/Analytics';

export default function PricingContent() {
  const { t } = useTranslation();
  const [billingType, setBillingType] = useState<BillingType>('monthly');
  // A/B: price-anchor copy under the hero. Server renders 'control';
  // the sticky per-browser variant is applied after mount.
  const [abVariant, setAbVariant] = useState<AbVariant>('control');

  // Read after mount rather than via useSearchParams(): that hook forces a
  // Suspense boundary which excluded this whole page from the static shell.
  useEffect(() => {
    const b = getQueryParam('billing');
    if (b === 'yearly' || b === 'monthly') {
      setBillingType(b);
    }
  }, []);

  useEffect(() => {
    const variant = getAbVariant('pricing_anchor');
    setAbVariant(variant);
    trackAbExposure('pricing_anchor', variant);
  }, []);

  // GA4 ecommerce: fire once per mount when the pricing table renders. Empty
  // dep array is the guard -- PRICING_TIERS is a module-level constant, not
  // state, so there is no re-render that should re-fire this.
  useEffect(() => {
    trackViewItemList(PRICING_TIERS);
  }, []);
  const { ref: heroRef, isVisible: heroVisible } = useIntersectionObserver({ threshold: 0.1 });
  const { ref: cardsRef, isVisible: cardsVisible } = useIntersectionObserver({ threshold: 0.1 });
  const { ref: ctaRef, isVisible: ctaVisible } = useIntersectionObserver({ threshold: 0.1 });

  const getPrice = (tier: typeof PRICING_TIERS[0]) => {
    if (billingType === 'yearly') {
      return tier.priceYearly / 12; // Show monthly equivalent
    }
    return tier.priceMonthly;
  };

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(0)}`;
  };

  // Cheapest cost per matched investor across every tier, at the billing
  // interval currently selected. Derived rather than written into the copy:
  // the previous hardcoded "$3.50" survived a reprice and was advertising a
  // number five times the real one. Rounded UP to the cent so the claim can
  // never undersell what a customer actually pays.
  const lowestCostPerInvestor = Math.min(
    ...PRICING_TIERS.map((tier) => getPrice(tier) / tier.vcCount)
  );
  const lowestCostPerInvestorLabel = `$${(Math.ceil(lowestCostPerInvestor) / 100).toFixed(2)}`;

  // Feature icon configuration based on tier and feature type.
  // Volume (10 -> 50 -> 150 contacts) is the only thing that genuinely differs
  // between tiers, so the volume row is the only one that gets a distinguishing
  // icon: purple on Professional, amber star on Scale. Do not add a key here
  // unless the tier actually gates it.
  const getFeatureIcon = (tierId: string, featureKey: string) => {
    // Features that change/upgrade between tiers (10 -> 50 -> 150 VCs)
    const changesOnPro = ['automatedSending50'];
    const changesOnScale = ['automatedSending150'];
    // Features that are brand new additions to a tier
    // Empty: volume is the only real tier difference, so no feature row
    // gets a distinguishing icon. See types/index.ts.
    const newOnScale: string[] = [];

    if (tierId === 'starter') {
      // Starter: all green checks
      return <Check className="w-3.5 h-3.5 text-green-600" />;
    } else if (tierId === 'professional') {
      // Professional: purple for changes/upgrades, green for same
      if (changesOnPro.includes(featureKey)) {
        return <Check className="w-3.5 h-3.5 text-purple-600" />;
      }
      return <Check className="w-3.5 h-3.5 text-green-600" />;
    } else {
      // Scale: star for changes from pro and new exclusive features, green for same
      if (changesOnScale.includes(featureKey) || newOnScale.includes(featureKey)) {
        return <Star className="w-3.5 h-3.5 text-amber-500 drop-shadow-[0_0_3px_rgba(245,158,11,0.5)]" />;
      }
      return <Check className="w-3.5 h-3.5 text-green-600" />;
    }
  };

  const getFeatureIconBg = (tierId: string, featureKey: string) => {
    const changesOnPro = ['automatedSending50'];
    const changesOnScale = ['automatedSending150'];
    // Empty: volume is the only real tier difference, so no feature row
    // gets a distinguishing icon. See types/index.ts.
    const newOnScale: string[] = [];

    if (tierId === 'starter') {
      return 'bg-green-100';
    } else if (tierId === 'professional') {
      if (changesOnPro.includes(featureKey)) {
        return 'bg-purple-100';
      }
      return 'bg-green-100';
    } else {
      if (changesOnScale.includes(featureKey) || newOnScale.includes(featureKey)) {
        return 'bg-amber-100 shadow-[0_0_8px_rgba(245,158,11,0.3)]';
      }
      return 'bg-green-100';
    }
  };

  return (
    <>
      {/* LAUNCH20 banner */}
      <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-sm text-center py-2 px-4">
        <span className="font-semibold">🚀 Launch offer:</span> use code{' '}
        <code className="bg-white border border-amber-300 rounded px-1.5 py-0.5 font-mono text-xs">LAUNCH20</code>{' '}
        for 20% off your first month — auto-applied at checkout.
      </div>

      {/* Pricing Hero */}
      <section className="pt-16 md:pt-24 pb-12 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div ref={heroRef} className="container text-center relative z-10">
          <div className={`fade-in-section ${heroVisible ? 'is-visible' : ''}`}>
            <div className="inline-block bg-primary-50 rounded-full px-6 py-2 text-primary-600 font-medium text-sm mb-6 animate-pulse-shadow">
              {t('pageContent.pricing.hero.tagline')}
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              {t('pageContent.pricing.hero.title')}
            </h1>
            {/*
              This line is the section heading for the plans grid below, so it is an
              <h2> rather than a <p>: the outline was h1 -> h3 (tier names) with no h2,
              which both axe and Google's page-structure heuristics flag. Copy is the
              existing hero.subtitle key — no new string introduced.
              CSS-neutrality: globals.css applies var(--font-playfair) to every h1-h6,
              so the body font is pinned back on explicitly here, and the size/weight
              the <p> inherited are stated as classes. Renders pixel-identical.
            */}
            <h2 className="text-xl font-normal font-[family-name:var(--font-inter)] text-gray-600 mb-4 max-w-3xl mx-auto">
              {t('pageContent.pricing.hero.subtitle')}
            </h2>
            <p className="text-base text-gray-500 mb-12 max-w-2xl mx-auto" suppressHydrationWarning>
              {t(
                abVariant === 'variant'
                  ? 'pageContent.pricing.hero.anchorVariant'
                  : 'pageContent.pricing.hero.anchorControl',
                {
                  perInvestor: lowestCostPerInvestorLabel,
                  fromPrice: formatPrice(Math.min(...PRICING_TIERS.map((tier) => getPrice(tier)))),
                }
              )}
            </p>

            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-4">
              <div className="inline-flex items-center bg-gray-100 rounded-full p-1">
                <button
                  onClick={() => setBillingType('monthly')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    billingType === 'monthly'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {t('pageContent.pricing.billingToggle.monthly')}
                </button>
                <button
                  onClick={() => setBillingType('yearly')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all relative ${
                    billingType === 'yearly'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {t('pageContent.pricing.billingToggle.yearly')}
                  <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                    {t('pageContent.pricing.billingToggle.savePercent')}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Background decoration */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-secondary-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-16 md:pb-20 bg-gradient-to-b from-white to-gray-50">
        <div ref={cardsRef} className="container">
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {PRICING_TIERS.map((tier, index) => (
              <div
                key={tier.id}
                className={`relative bg-white rounded-2xl shadow-xl overflow-hidden hover-lift fade-in-section ${cardsVisible ? 'is-visible' : ''} ${
                  tier.highlighted ? 'border-2 border-primary-500 ring-4 ring-primary-100' : 'border border-gray-200'
                }`}
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                {tier.highlighted && (
                  <div className="absolute top-0 left-0 right-0 bg-primary-500 text-white text-center py-1 text-sm font-medium">
                    {t(`pageContent.pricing.tiers.${tier.id}.badge`)}
                  </div>
                )}
                
                <div className={`p-8 ${tier.highlighted ? 'pt-10' : ''}`}>
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">
                    {t(`pageContent.pricing.tiers.${tier.id}.name`)}
                  </h3>
                  <p className="text-gray-500 text-sm mb-4">
                    {t(`pageContent.pricing.tiers.${tier.id}.subtitle`)}
                  </p>
                  
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-gray-900">
                      {formatPrice(getPrice(tier))}
                    </span>
                    <span className="text-gray-500">/mo</span>
                    {billingType === 'yearly' && (
                      <p className="text-sm text-green-600 mt-1">
                        Billed annually {formatPrice(tier.priceYearly)}/year
                      </p>
                    )}
                  </div>

                  <div className="bg-primary-50 rounded-lg px-4 py-2 mb-6 text-center">
                    <span className="font-semibold text-primary-700">
                      {t(`pageContent.pricing.tiers.${tier.id}.vcCount`)}
                    </span>
                  </div>

                  {/*
                    Every tier lists its full feature set. The three lists are
                    near-identical by design — only the volume row differs — but
                    a complete lane per plan is far easier to scan than a shared
                    block, so the repetition is deliberate. The icon marks the
                    step up: green on Starter, purple on Professional, amber
                    star on Scale.
                  */}
                  <ul className="space-y-2.5 mb-8">
                    {tier.features.map((featureKey) => (
                      <li key={featureKey} className="flex items-start gap-2.5">
                        <div className={`rounded-full p-1 flex-shrink-0 ${getFeatureIconBg(tier.id, featureKey)}`}>
                          {getFeatureIcon(tier.id, featureKey)}
                        </div>
                        <span className="text-gray-700 text-sm leading-snug">
                          {t(`pageContent.pricing.tiers.${tier.id}.features.${featureKey}`)}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/*
                    The anchor is the interactive element, so it carries the button
                    styling itself via buttonVariants() instead of wrapping a <button>
                    (an <a> may not contain a <button>). fullWidth supplies w-full,
                    which is what the old `block` wrapper was there for.
                  */}
                  <Link
                    href={`/?tier=${tier.id}&billing=${billingType}#upload-section`}
                    className={cn(
                      buttonVariants({
                        variant: tier.highlighted ? 'default' : 'outline',
                        size: 'lg',
                        fullWidth: true,
                      }),
                      'group'
                    )}
                    onClick={() => {
                      trackPricingSelection(tier.id, billingType, abVariant);
                      trackSelectItem(tier, billingType);
                    }}
                  >
                    {t('pageContent.pricing.cta.button')}
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-gradient-to-r from-primary-500 to-primary-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-10"></div>
        <div ref={ctaRef} className={`container text-center relative z-10 fade-in-section ${ctaVisible ? 'is-visible' : ''}`}>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t('pageContent.pricing.cta.title')}
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            {t('pageContent.pricing.cta.subtitle')}
          </p>
          <div className="inline-block">
            <Link
              href="/#upload-section"
              className={cn(
                buttonVariants({ variant: 'secondary', size: 'lg' }),
                'px-8 shadow-lg group hover-scale'
              )}
            >
              {t('pageContent.pricing.cta.button')}
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
        
        {/* Background decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-secondary-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
      </section>
    </>
  );
} 
